import { useTranslation } from '../i18n';

import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, Typography, Divider, Result, Button, Form, Input, Spin, message } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import authService from '../services/authService';

const { Title } = Typography;

/**
 * 邮箱验证页 /verify-email?token=xxx
 * - 带 token：自动调用验证接口，成功引导登录，失败展示重发表单
 * - 不带 token：直接展示重发验证邮件表单
 */
const VerifyEmail = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    // verifying | success | error | no-token
    const [status, setStatus] = useState(token ? 'verifying' : 'no-token');
    const [resultInfo, setResultInfo] = useState(null);
    const [resendLoading, setResendLoading] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();

    // token 是一次性的：React 18 StrictMode 下 effect 会执行两次，
    // 用 ref 防止第二次调用把刚验证成功的 token 当作"无效"。
    const requestedRef = useRef(false);

    useEffect(() => {
        if (!token || requestedRef.current) return;
        requestedRef.current = true;

        (async () => {
            const result = await authService.verifyEmail(token);
            setResultInfo(result);
            setStatus(result.success ? 'success' : 'error');
        })();
    }, [token]);

    const onResend = async (values) => {
        try {
            setResendLoading(true);
            const result = await authService.resendVerification(values.email);
            if (result.success) {
                messageApi.success(result.message || t('Verification email resent. Please check your inbox'));
            } else {
                messageApi.error(result.message || t('Failed to send verification email'));
            }
        } finally {
            setResendLoading(false);
        }
    };

    const resendForm = (
        <Form name="resend-verification" onFinish={onResend} size="large">
            <Form.Item
                name="email"
                rules={[
                    { required: true, message: t('Enter the email address you registered with!') },
                    { type: 'email', message: t('Enter a valid email address"\'!') }
                ]}
            >
                <Input prefix={<MailOutlined />} placeholder={t('Registration email')} />
            </Form.Item>

            <Form.Item>
                <Button type="primary" htmlType="submit" style={{ width: '100%' }} loading={resendLoading}>
                    {t('Resend verification email')}
                </Button>
                <div style={{ marginTop: 16, textAlign: 'center' }}>
                    <Link to="/login">{t('Back to sign in')}</Link>
                </div>
            </Form.Item>
        </Form>
    );

    const renderContent = () => {
        if (status === 'verifying') {
            return (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>{t('Verifying your email…')}</div>
                </div>
            );
        }

        if (status === 'success') {
            return (
                <Result
                    status="success"
                    title={t('Email verified')}
                    subTitle={
                        resultInfo?.activated
                            ? t('Your account is now active. You can sign in.')
                            : t('Email verified. Please wait for administrator approval before signing in.')
                    }
                    extra={
                        <Link to="/login">
                            <Button type="primary">{t('Sign in')}</Button>
                        </Link>
                    }
                />
            );
        }

        if (status === 'error') {
            return (
                <>
                    <Result
                        status="error"
                        title={t('Verification failed')}
                        subTitle={resultInfo?.message || resultInfo?.error || t('The verification link is invalid or has expired')}
                        style={{ paddingBottom: 8 }}
                    />
                    <Divider style={{ margin: '8px 0 16px' }}>{t('Resend verification email')}</Divider>
                    {resendForm}
                </>
            );
        }

        // no-token：直接展示重发表单
        return (
            <>
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    {t('Enter your registered email address to resend the verification email.')}
                </div>
                {resendForm}
            </>
        );
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f0f2f5' }}>
            {contextHolder}
            <Card style={{ width: 440, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                    <Title level={2} style={{ margin: 0 }}>{t('Verify email')}</Title>
                    <Divider style={{ margin: '16px 0' }} />
                </div>
                {renderContent()}
            </Card>
        </div>
    );
};

export default VerifyEmail;
